import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { h6, paragraphSmall } from '@helpers/typography';
import { horizontalFadeIn, staggerChildren } from '@helpers/variants';
import HexagonSvg from '@img/shapes/hexagon-solid.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import ImageMaskStyles from '@stories/Components/Global/ImageMask/ImageMask.styles';
import { PillStyles } from '@stories/Components/Pill/Pill.styles';
import { m } from 'framer-motion';
import { transparentize } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled(m.div).attrs(({ inView, animateUp }) => ({
    initial: 'hidden',
    animate: inView ? 'visible' : 'hidden',
    variants: {
        hidden: {
            y: animateUp ? 50 : 0,
            opacity: 0,
        },
        visible: {
            y: 0,
            opacity: 1,
        },
    },
    transition: {
        duration: 0.35,
    },
})) `
  position: relative;

  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  --colour: ${({ colour }) => (colour === 'buff' ? brand.buff.buff4 : brand.tertiary.pinkAlt)};

  &::before {
    content: '';
    position: absolute;
    background-color: ${brand.white};

    left: 0;
    width: calc(100% - 8px);
    top: 20%;
    // Make border line up with halfway up standard size button //
    height: calc(80% - 8px - (58px / 2));
    @media ${from(Device.TabletLarge)} {
      width: 70%;
      top: 0;
      height: 100%;
    }

    border: 4px solid var(--colour);
    border-radius: 15px;
    z-index: 0;
    box-shadow: 4px 4px 12px 0px ${transparentize(0.9, brand.black)};
  }
`;
const ContentWrapper = styled(m.div).attrs(() => ({
    variants: staggerChildren(0.1),
})) `
  padding: 30px 32px;
  z-index: 1;

  @media ${until(Device.TabletLarge)} {
    align-items: center;
    text-align: center;
    padding: 0px 15px;
    position: relative;
    z-index: 5;
  }

  display: flex;
  flex-direction: column;
`;
const CategoryPill = styled(m.div).attrs(() => ({
    variants: horizontalFadeIn(-25),
})) `
  ${PillStyles.Container} {
    font-size: ${fluidAdjusted(12, 13)};
    line-height: ${fluidAdjusted(18, 19)};
    letter-spacing: -0.024em;
    font-weight: 500;
    padding: 6px 12px 7px;
  }
`;
const Date = styled.div `
  font-size: 16px;
  line-height: 28px;
  font-weight: 100;
`;
const Title = styled(m.div).attrs(() => ({
    variants: horizontalFadeIn(-25),
})) `
  ${h6}
  text-decoration: none;

  @media ${until(Device.TabletLarge)} {
    max-width: 70%;
  }
`;
const Text = styled(m.div).attrs(() => ({
    variants: horizontalFadeIn(-25),
})) `
  font-size: 15px;
  line-height: 24px;
  letter-spacing: -0.5px;
  font-weight: 100;
  margin: 0 0 ${fluidAdjusted(16, 18)};
`;
const LocationWrapper = styled(m.a).attrs(() => ({
    variants: horizontalFadeIn(-25),
})) `
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: center;
  ${paragraphSmall};
  text-decoration: none;

  svg {
    width: 24px;
  }

  span {
    position: relative;
    bottom: 2px;
  }
`;
const ButtonWrapper = styled(m.div).attrs(() => ({
    variants: horizontalFadeIn(-25),
})) `
  position: relative;
  margin-top: auto;
  width: max-content;
  max-width: 100%;

  ${ButtonStyles.Button} {
    --textColour: ${brand.black} !important;

    @media ${until(Device.TabletLarge)} {
      &:hover {
        ${ButtonStyles.ButtonInner} {
          background-color: var(--brandColour);
        }

        ${ButtonStyles.Fill}, ${ButtonStyles.Fill} {
          opacity: 1;
          color: var(--brandColour);
        }
      }
    }
  }
`;
const AddToCalendarWrapper = styled.div `
  --buttonSideOffset: 21px;
  --optionSize: 32px;
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  right: 0;
  margin: auto;
  width: fit-content;
  min-width: calc(100% - (var(--buttonSideOffset) * 2));
  z-index: 5;

  ${({ open }) => !open &&
    css `
      pointer-events: none;
    `};

  .chq-atc {
    width: 100%;
    height: calc(var(--optionSize) * 5);

    &--dropdown {
      position: relative;
      border: 2px solid var(--colour);
      border-radius: 0px 0px 5px 5px;
      box-shadow: none;

      a {
        color: ${brand.black};
        height: var(--optionSize);
      }
    }

    &--button {
      display: none;
    }
  }
`;
const ImageWrapper = styled(m.div).attrs(() => ({
    variants: horizontalFadeIn(25, 0.85),
})) `
  --padding: 10px;
  padding: var(--padding);
  aspect-ratio: 1 / 1;
  position: relative;
  margin: auto;
  width: 100%;
  display: flex;
  align-items: center;

  img {
    pointer-events: none;
  }

  @media ${until(Device.TabletLarge)} {
    max-width: 275px;
    order: -1;
  }
`;
const ShapeBg = styled(HexagonSvg) `
  position: absolute;
  top: 0;
  left: 0;
  color: var(--colour);
  z-index: 1;
  height: 100%;
  width: 100%;
`;
const ShapeBgWhite = styled(HexagonSvg) `
  --offset: 4px;
  position: absolute;
  top: var(--offset);
  left: var(--offset);
  color: ${brand.white};
  height: calc(100% - (var(--offset) * 2));
  width: calc(100% - (var(--offset) * 2));
  z-index: 2;
`;
const ShapeContent = styled.div `
  --offset: 8px;
  position: absolute;
  top: var(--offset);
  left: var(--offset);
  color: ${brand.white};
  height: calc(100% - (var(--offset) * 2));
  width: calc(100% - (var(--offset) * 2));
`;
const ImageContainer = styled.div `
  position: relative;
  z-index: 3;
  height: calc(100% - var(--padding) - ${fluidAdjusted(0, 8, Device.Tablet, Device.ActualDesktop)});
  display: flex;
  align-items: center;

  ${ImageMaskStyles.Container} {
    position: relative;
  }
`;
const Image = styled.img ``;
const ImageLogo = styled.div `
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 25%;
  background-color: ${({ colour }) => transparentize(0.2, colour === 'buff' ? brand.buff.buff4 : brand.tertiary.pinkAlt)};
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    height: 75%;
  }
`;
const WrapperLink = styled.a `
  text-decoration: none;
  color: inherit;

  &:focus {
    outline: 0;
  }
`;
export const EventCardStyles = {
    Container,
    ContentWrapper,
    ImageWrapper,
    CategoryPill,
    Date,
    Title,
    Text,
    LocationWrapper,
    ButtonWrapper,
    ShapeBg,
    ShapeBgWhite,
    ShapeContent,
    ImageContainer,
    Image,
    ImageLogo,
    AddToCalendarWrapper,
    WrapperLink,
};
